import React, { useEffect } from "react";

import StaticText from "./static-text/cookie-policy-en.json";
import { Link } from "react-router-dom";
import { Link as MotifLink } from "common/components";
import StaticCookies from "assets/static/cookie.json";
import CookieList from "common/components/custom/cookie/cookie-list";
import Page from "../../page/Page";
import { ROUTE_PATH } from "common/config";
import { WindowUtil } from "common";

import "./CookiePolicy.scss";

enum SectionId {
  WEB_BEACONS = "what-are-web-beacons",
  COOKIE_DEFINITION = "what-is-a-cookie",
  COOKIE_TYPES = "what-types-of-cookies-do-we-use",
  CONTROL_COOKIES = "how-to-control-or-delete-cookies",
  PAST_COOKIES = "cookies-that-have-been-set-in-the-past",
  POLICY_CHANGE = "changes-to-this-cookie-policy"
}

const CookiePolicy: React.FC = () => {
  useEffect(() => {
    WindowUtil.scrollToTop();
  }, []);

  const lastUpdated = "February 2022";
  const cookieSettingsPageURL = `https://${window.location.host}/${ROUTE_PATH.COOKIE_SETTINGS}`;
  const cookies = [
    ...StaticCookies.required,
    ...StaticCookies.functional,
    ...StaticCookies.performance,
    ...StaticCookies.advertising
  ].sort((cookie1, cookie2) => (cookie1.provider > cookie2.provider ? 1 : -1));

  const getUrl = (id: SectionId) => {
    return `${ROUTE_PATH.COOKIE_POLICY}#${id}`;
  };

  return (
    <Page title={StaticText.PAGE_TITLE} className="cookie-policy">
      <div className="cookie-policy-content">
        <ul className="cookie-policy-content__links">
          <li>
            {"1. "}
            <MotifLink
              href={getUrl(SectionId.WEB_BEACONS)}
              className="cookie-policy-content__link"
            >
              {StaticText.WEB_BEACONS_HEADING}
            </MotifLink>
          </li>
          <li>
            {"2. "}
            <MotifLink
              href={getUrl(SectionId.COOKIE_DEFINITION)}
              className="cookie-policy-content__link"
            >
              {StaticText.COOKIE_DEFINITION_HEADING}
            </MotifLink>
          </li>
          <li>
            {"3. "}
            <MotifLink
              href={getUrl(SectionId.COOKIE_TYPES)}
              className="cookie-policy-content__link"
            >
              {StaticText.COOKIE_TYPES_HEADING}
            </MotifLink>
          </li>
          <li>
            {"4. "}
            <MotifLink
              href={getUrl(SectionId.CONTROL_COOKIES)}
              className="cookie-policy-content__link"
            >
              {StaticText.CONTROL_COOKIES_HEADING}
            </MotifLink>
          </li>
          <li>
            {"5. "}
            <MotifLink
              href={getUrl(SectionId.PAST_COOKIES)}
              className="cookie-policy-content__link"
            >
              {StaticText.PAST_COOKIES_HEADING}
            </MotifLink>
          </li>
          <li>
            {"6. "}
            <MotifLink
              href={getUrl(SectionId.POLICY_CHANGE)}
              className="cookie-policy-content__link"
            >
              {StaticText.POLICY_CHANGE_HEADING}
            </MotifLink>
          </li>
        </ul>
        <p>{`${StaticText.LAST_UPDATED}: ${lastUpdated}`}</p>
        <p>{StaticText.P1}</p>
        <p>
          {StaticText.P2_PART1}
          <Link
            to={`/${ROUTE_PATH.COOKIE_SETTINGS}`}
            className="cookie-policy-content__settings-link"
          >
            {cookieSettingsPageURL}
          </Link>
          {StaticText.P2_PART2}
        </p>

        <section>
          <h2 id={SectionId.WEB_BEACONS} className="section-heading">
            {StaticText.WEB_BEACONS_HEADING}
          </h2>
          <p>{StaticText.WEB_BEACONS_P1}</p>
        </section>

        <section>
          <h2 id={SectionId.COOKIE_DEFINITION} className="section-heading">
            {StaticText.COOKIE_DEFINITION_HEADING}
          </h2>
          <p>{StaticText.COOKIE_DEFINITION_P1}</p>
          <p>{StaticText.COOKIE_DEFINITION_P2}</p>
          <p>{StaticText.COOKIE_DEFINITION_P3}</p>
          <div className="cookie-policy-content__list-container">
            <span>{StaticText.COOKIE_DEFINITION_CATEGORY_HEADING}</span>
            <ul className="cookie-policy-content__list">
              <li>{StaticText.COOKIE_DEFINITION_FIRST_PARTY}</li>
              <li>{StaticText.COOKIE_DEFINITION_THIRD_PARTY}</li>
            </ul>
          </div>
          <p>{StaticText.COOKIE_DEFINITION_P4}</p>
          <h3>{StaticText.DO_NOT_TRACK_HEADING}</h3>
          <p>{StaticText.COOKIE_DEFINITION_P5}</p>
        </section>

        <section>
          <h2 id={SectionId.COOKIE_TYPES} className="section-heading">
            {StaticText.COOKIE_TYPES_HEADING}
          </h2>
          <div className="cookie-policy-content__list-container">
            <span>{StaticText.COOKIE_TYPES_TYPE_HEADING}</span>
            <ul className="cookie-policy-content__list">
              <li>{StaticText.COOKIE_TYPES_REQUIRED}</li>
              <li>{StaticText.COOKIE_TYPES_PERFORMANCE}</li>
              <li>{StaticText.COOKIE_TYPES_FUNCTIONAL}</li>
              <li>{StaticText.COOKIE_TYPES_ADVERTISING}</li>
            </ul>
          </div>
          <p>{StaticText.COOKIE_TYPES_P1}</p>
          <p>{StaticText.COOKIE_TYPES_COOKIE_LIST_HEADING}</p>
          <CookieList items={cookies} />
        </section>

        <section>
          <h2 id={SectionId.CONTROL_COOKIES} className="section-heading">
            {StaticText.CONTROL_COOKIES_HEADING}
          </h2>
          <p>{StaticText.CONTROL_COOKIES_P1}</p>
          <p>
            {StaticText.CONTROL_COOKIES_P2_PART1}
            <Link
              to={`/${ROUTE_PATH.COOKIE_SETTINGS}`}
              className="cookie-policy-content__settings-link"
            >
              {cookieSettingsPageURL}
            </Link>
            .
          </p>
          <p>{StaticText.CONTROL_COOKIES_P3}</p>
          <p>{StaticText.CONTROL_COOKIES_P4}</p>
        </section>

        <section>
          <h2 id={SectionId.PAST_COOKIES} className="section-heading">
            {StaticText.PAST_COOKIES_HEADING}
          </h2>
          <p>{StaticText.PAST_COOKIES_P1}</p>
        </section>

        <section>
          <h2 id={SectionId.POLICY_CHANGE} className="section-heading">
            {StaticText.POLICY_CHANGE_HEADING}
          </h2>
          <p>{StaticText.POLICY_CHANGE_P1}</p>
        </section>
      </div>
    </Page>
  );
};

export default CookiePolicy;
